import React from "react";
import { observer } from "mobx-react";
import { ArrowForward, Warning, Error as ErrorIcon, AutoAwesome } from "@mui/icons-material";
import { Button } from "@ann/web-components";
import { useContactsMapping } from "../../hooks";
import { SYSTEM_FIELD_MAPPINGS } from "../../constants/blastConstants";
import {
  Container,
  Header,
  Title,
  MappingGrid,
  MappingRow,
  MappingLabel,
  ArrowIcon,
  SelectWrapper,
  ColumnHeaders,
  ColumnHeader,
  ValidationMessage,
  ActionButtons,
  AutoMappedBanner,
  SampleValue,
  SelectWithSample,
  PreviewSection,
  PreviewHeader,
  PreviewTitle,
  PreviewBadge,
  PreviewTable,
  RowNumber,
  EmptyCell,
} from "./FieldMapper.styled";

const FieldMapper = observer(({ onSave }) => {
  const {
    fileUploadedHeaders,
    fieldMappings,
    isCsvMapped,
    mappingValidation,
    duplicateCheck,
    autoMappedCount,
    previewRows,
    mappedFieldsList,
    handleFieldMappingChange,
    saveMappings,
    getColumnSampleValue,
  } = useContactsMapping();

  const handleSave = () => {
    const result = saveMappings();
    if (result.success) {
      onSave?.();
    }
  };

  if (!fileUploadedHeaders.length) {
    return null;
  }

  if (isCsvMapped) {
    return (
      <Container>
        <ValidationMessage $type="success">
          Field mappings saved successfully. You can now configure your email template.
        </ValidationMessage>
      </Container>
    );
  }

  // Get field display name from mapping
  const getFieldDisplayName = (mapping) => {
    const field = SYSTEM_FIELD_MAPPINGS.find((f) => f.mapping === mapping);
    return field?.name || mapping;
  };

  return (
    <Container>
      <Header>
        <Title>Map CSV Columns to Contact Fields</Title>
      </Header>

      {autoMappedCount > 0 && (
        <AutoMappedBanner>
          <AutoAwesome />
          {autoMappedCount} field{autoMappedCount !== 1 ? "s" : ""} auto-mapped. Please review and adjust if needed.
        </AutoMappedBanner>
      )}

      <ColumnHeaders>
        <ColumnHeader>CSV Column (with sample)</ColumnHeader>
        <span />
        <ColumnHeader>System Field</ColumnHeader>
      </ColumnHeaders>

      <MappingGrid>
        {SYSTEM_FIELD_MAPPINGS.map((systemField) => {
          const selectedColumn = fieldMappings[systemField.mapping];
          const sampleValue = selectedColumn ? getColumnSampleValue(selectedColumn) : "";

          return (
            <MappingRow key={systemField.mapping}>
              <SelectWithSample>
                <SelectWrapper>
                  <select
                    value={selectedColumn || ""}
                    onChange={(e) =>
                      handleFieldMappingChange(systemField.mapping, e.target.value)
                    }
                    aria-label={`Map CSV column to ${systemField.name}`}
                  >
                    <option value="">Select column...</option>
                    {fileUploadedHeaders.map((header) => (
                      <option key={header.value} value={header.value}>
                        {header.name}
                      </option>
                    ))}
                  </select>
                </SelectWrapper>
                {sampleValue && (
                  <SampleValue title={sampleValue}>
                    e.g. "{sampleValue}"
                  </SampleValue>
                )}
              </SelectWithSample>

              <ArrowIcon>
                <ArrowForward />
              </ArrowIcon>

              <MappingLabel>
                {systemField.name}
                {systemField.required && <span className="required">*</span>}
              </MappingLabel>
            </MappingRow>
          );
        })}
      </MappingGrid>

      {!mappingValidation.isValid && (
        <ValidationMessage $type="error">
          <ErrorIcon />
          Required fields missing: {mappingValidation.missingFields.join(", ")}
        </ValidationMessage>
      )}

      {duplicateCheck.hasDuplicates && (
        <ValidationMessage $type="warning">
          <Warning />
          {duplicateCheck.duplicates.length} duplicate email
          {duplicateCheck.duplicates.length !== 1 ? "s" : ""} found. 
          Duplicates will be skipped when sending.
        </ValidationMessage>
      )}

      {/* Preview Table */}
      {previewRows.length > 0 && mappedFieldsList.length > 0 && (
        <PreviewSection>
          <PreviewHeader>
            <PreviewTitle>Data Preview</PreviewTitle>
            <PreviewBadge>First {previewRows.length} rows</PreviewBadge>
          </PreviewHeader>
          <PreviewTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                {mappedFieldsList.map((field) => (
                  <th key={field}>{getFieldDisplayName(field)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row) => (
                <tr key={row._rowIndex}>
                  <RowNumber>{row._rowIndex}</RowNumber>
                  {mappedFieldsList.map((field) => (
                    <td key={field}>
                      {row[field] || <EmptyCell>—</EmptyCell>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </PreviewTable>
        </PreviewSection>
      )}

      <ActionButtons>
        <Button
          type="primary"
          size="medium"
          onClick={handleSave}
          disabled={!mappingValidation.isValid}
          label="Confirm Contacts"
        />
      </ActionButtons>
    </Container>
  );
});

export default FieldMapper;
